/**
 * **Wie frisch der Bestand ist** ([E-312](../../../docs/ENTSCHEIDUNGEN.md#e-312), 02.09.2026).
 *
 * Aus Ticket NCW-6X848M: *„Statt «Live verbunden» unten links die Zeit bis zum Sync anzeigen."*
 *
 * Unten links stand bis hierher der Zustand der **WebSocket-Verbindung** – „Live verbunden",
 * „Verbindet …", „Getrennt". Das ist eine wahre Auskunft über die falsche Frage: sie sagt, ob
 * mein Browser mit dem Server spricht, nicht, wie alt das ist, was er mir zeigt. Die Verbindung
 * kann tadellos stehen, während der GitLab-Abgleich seit einer Stunde hängt.
 *
 * ## Warum die **älteste** Quelle zählt
 *
 * So von Nils entschieden (02.09.2026). Es gibt nicht einen Abgleich, sondern drei, die den
 * Aufgabenbestand füllen – mit verschiedenen Takten. „Vor 2 Minuten" wäre für den schnellsten
 * richtig und für den Bestand als Ganzes eine Beschönigung: wenn NetCon seit 40 Minuten steht,
 * ist der Bestand 40 Minuten alt, gleich wie fleißig GitLab war.
 *
 * Eine Zeile, die im Zweifel zu **alt** meldet, ist die einzige, der man glauben kann.
 *
 * ## Was **nicht** mitzählt
 *
 * | Job | warum nicht |
 * |---|---|
 * | `gitlab.write-back` | schreibt **hinaus** – über die Frische des Bestands sagt er nichts |
 * | `intranet.absences` | bringt Abwesenheiten, keine Aufgaben |
 * | `netcon.abwanderung`, `gitlab.abwanderung` | räumen auf, täglich – sie würden die Zahl dauerhaft alt aussehen lassen |
 *
 * ## Warum die Regel hier steht und nicht im Zeitgeber
 *
 * Dieselbe eiserne Regel wie in `erinnerung.ts` und `vorwarnung.ts`: **kein `new Date()` ohne
 * Übergabe.** Eine Anzeige, für deren Prüfung man erst einen Konnektor anhalten muss, wird nicht
 * geprüft.
 */

/**
 * **Die Quellen, die den Bestand füllen** – mit dem Takt, in dem sie laufen.
 *
 * **Die Takte stehen nur hier.** Die drei Zeitgeber in `connectors/jobs.ts` und
 * `mail/versand.ts` holen ihren Vorgabewert über {@link abgleichTaktMs} – sonst wären es zwei
 * Zahlen für dasselbe, und die Anzeige rechnete irgendwann gegen einen Takt, den es nicht mehr
 * gibt. Ein Takt, den man ändert, ändert damit auch die Vorhersage „nächster in 3 Min".
 */
export const ABGLEICH_QUELLEN = [
  { job: 'gitlab.incremental', label: 'GitLab', abstandMinuten: 5 },
  { job: 'netcon.sync', label: 'NetCon', abstandMinuten: 15 },
  // 1 statt 5 Minuten (22.10.2026, Nils: Plaud-Aufnahmen kommen per Mail und sollen schnell da sein).
  { job: 'mail.eingang', label: 'Postfach', abstandMinuten: 1 },
] as const

export interface AbgleichLauf {
  job: string
  /** Wann dieser Job zuletzt **durchgelaufen** ist. `null` heißt „noch nie". */
  zuletzt: Date | string | null
}

export interface AbgleichStand {
  /** Der letzte Lauf der **ältesten** Quelle – `null`, wenn eine davon noch nie lief. */
  zuletzt: Date | null
  /** Wann sie planmäßig wieder dran ist. `null`, wenn es keinen letzten Lauf gibt. */
  naechster: Date | null
  /** Welche Quelle das ist – sie steht im Tooltip, damit die Zahl nachprüfbar ist. */
  quelle: string
  /**
   * **Der Takt ist gerissen.** Nicht schon beim ersten verpassten Lauf: ein Job, der gerade
   * arbeitet, ist ein paar Sekunden „zu spät", und eine Anzeige, die dabei rot wird, ist nach
   * zwei Tagen Hintergrundrauschen. Erst beim **dritten** ausgefallenen Takt.
   */
  haengt: boolean
}

/** Minuten zwischen zwei Zeitpunkten, abgerundet – „vor 0 Minuten" heißt „gerade eben". */
function minutenSeit(von: Date, jetzt: Date): number {
  return Math.max(0, Math.floor((jetzt.getTime() - von.getTime()) / 60_000))
}

/**
 * **Der Stand des Abgleichs** – die älteste Quelle gewinnt.
 *
 * Eine Quelle, die **noch nie** gelaufen ist, schlägt jede andere: sie ist unendlich alt. Das
 * ist der Zustand kurz nach einem Neustart, und dort ist „unbekannt" die ehrliche Auskunft.
 */
export function abgleichStand(laeufe: readonly AbgleichLauf[], jetzt: Date): AbgleichStand {
  const jeJob = new Map(laeufe.map((l) => [l.job, l.zuletzt ? new Date(l.zuletzt) : null]))

  let aelteste: (typeof ABGLEICH_QUELLEN)[number] = ABGLEICH_QUELLEN[0]
  let aeltester: Date | null = jeJob.get(aelteste.job) ?? null

  for (const quelle of ABGLEICH_QUELLEN) {
    const wann = jeJob.get(quelle.job) ?? null
    if (wann === null) {
      /** Noch nie gelaufen – das ist der älteste denkbare Stand, und die Suche ist beendet. */
      return { zuletzt: null, naechster: null, quelle: quelle.label, haengt: true }
    }
    if (aeltester === null || wann < aeltester) {
      aeltester = wann
      aelteste = quelle
    }
  }

  if (aeltester === null)
    return { zuletzt: null, naechster: null, quelle: aelteste.label, haengt: true }

  const naechster = new Date(aeltester.getTime() + aelteste.abstandMinuten * 60_000)
  return {
    zuletzt: aeltester,
    naechster,
    quelle: aelteste.label,
    haengt: minutenSeit(aeltester, jetzt) > aelteste.abstandMinuten * 3,
  }
}

/**
 * **Der Satz, der unten links steht.**
 *
 * „Abgleich vor 2 Min · nächster in 3 Min" – zwei Zahlen, weil beide etwas anderes beantworten:
 * die erste *„wie alt ist, was ich sehe"*, die zweite *„muss ich warten oder neu laden"*.
 *
 * Ist der nächste Lauf rechnerisch schon vorbei, steht keine Wartezeit mehr da, sondern
 * „gleich" – eine negative Zahl wäre die Sorte Auskunft, die man zweimal liest.
 */
export function abgleichText(stand: AbgleichStand, jetzt: Date): string {
  if (stand.zuletzt === null) return 'Abgleich: noch kein Lauf'

  const her = minutenSeit(stand.zuletzt, jetzt)
  const alter = her === 0 ? 'gerade eben' : `vor ${her} Min`

  if (stand.haengt) return `Abgleich hängt – letzter ${alter}`

  if (stand.naechster === null) return `Abgleich ${alter}`
  const bis = Math.ceil((stand.naechster.getTime() - jetzt.getTime()) / 60_000)
  return `Abgleich ${alter} · nächster ${bis <= 0 ? 'gleich' : `in ${bis} Min`}`
}

/**
 * **Der Takt eines Abgleich-Jobs in Millisekunden** – der Vorgabewert seines Zeitgebers.
 *
 * Wirft nicht bei einem unbekannten Namen, sondern liefert `null`: ein Zeitgeber, der hier nicht
 * geführt wird (Abwanderung, Rückschreiben), hat seinen eigenen Takt und soll davon nichts
 * wissen müssen.
 */
export function abgleichTaktMs(job: string): number | null {
  const quelle = ABGLEICH_QUELLEN.find((q) => q.job === job)
  return quelle ? quelle.abstandMinuten * 60_000 : null
}
